import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import LeaderboardTeamDetail from './LeaderboardTeamDetail';
import LeaderboardBowlerDetail from './LeaderboardBowlerDetail';

const BACKEND_URL = process.env.REACT_APP_API_URL;

// Custom scrollbar styles
const scrollbarStyles = `
  .custom-scrollbar::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  .custom-scrollbar::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 10px;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: rgba(147, 51, 234, 0.5);
    border-radius: 10px;
    transition: background 0.2s;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: rgba(147, 51, 234, 0.8);
  }

  .custom-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: rgba(147, 51, 234, 0.5) rgba(255, 255, 255, 0.05);
  }
`;

interface LeaderboardProps {
  leagueId: number;
  leagueName?: string;
  staffRoles?: number[];
  onBack: () => void;
  onNavigateToFinancial?: () => void;
}

interface PayoutSetting {
  placement: number;
  amount: number;
  type: 'money' | 'item';
  item_desc?: string;
}

interface PayoutSettings {
  name: string;
  description: string;
  payouts: PayoutSetting[];
}

interface MostPointsEntry {
  team_id: number;
  team_name: string;
  points: number;
  placement: number;
}

interface OverallPinfallEntry {
  bowler_id: number;
  bowler_name: string;
  team_id: number;
  team_name: string;
  total_pinfall: number;
  games_played: number;
  average: number;
  division?: string; // Add division field
}

interface OverallTeamPinfallEntry {
  team_id: number;
  team_name: string;
  total_pinfall: number;
  games_played: number;
  average: number;
}

// Add state for division-specific pinfall data
interface DivisionPinfallData {
  [division: string]: OverallPinfallEntry[];
}

const Leaderboard: React.FC<LeaderboardProps> = ({ leagueId, leagueName, staffRoles, onBack, onNavigateToFinancial }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [payoutSettings, setPayoutSettings] = useState<PayoutSettings[]>([]);
  const [activeTab, setActiveTab] = useState<string>(''); // Changed to store unique key
  const [mostPointsData, setMostPointsData] = useState<MostPointsEntry[]>([]);
  const [overallPinfallData, setOverallPinfallData] = useState<OverallPinfallEntry[]>([]);
  const [overallTeamPinfallData, setOverallTeamPinfallData] = useState<OverallTeamPinfallEntry[]>([]);
  const [divisionPinfallData, setDivisionPinfallData] = useState<{ [division: string]: OverallPinfallEntry[] }>({});
  
  // Detail modal states
  const [selectedTeam, setSelectedTeam] = useState<{ id: number; name: string } | null>(null);
  const [selectedBowler, setSelectedBowler] = useState<{ id: number; name: string; teamName: string } | null>(null);
  
  // Add payout modal states
  const [showAddPayoutModal, setShowAddPayoutModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [newPayout, setNewPayout] = useState({
    name: 'Most Points',
    description: '',
    placement: 1,
    type: 'money' as 'money' | 'item',
    amount: 0,
    item_desc: ''
  });

  const fetchLeaderboardData = useCallback(async () => {
    try {
      setLoading(true);
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      
      // Clear existing data to prevent contamination
      setMostPointsData([]);
      setOverallPinfallData([]);
      setOverallTeamPinfallData([]);
      setDivisionPinfallData({});
      
      // Fetch payout settings
      const payoutResponse = await fetch(`${apiUrl}/admin/leaderboard/leagues/${leagueId}/payout-settings`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (payoutResponse.ok) {
        const payoutData = await payoutResponse.json();
        setPayoutSettings(payoutData);
        
        // Set active tab to first available leaderboard (using unique key)
        if (payoutData.length > 0) {
          const firstSetting = payoutData[0];
          const uniqueKey = `${firstSetting.name}|||${firstSetting.description}`;
          setActiveTab(uniqueKey);
        }
        
        // Check for division-specific Overall Pinfall leaderboards
        const divisionLeaderboards = payoutData.filter((setting: PayoutSettings) => 
          setting.name === 'Overall Pinfall' && 
          setting.description && 
          setting.description.startsWith('Division ')
        );
        
        // Fetch data for each division leaderboard
        const divisionData: { [division: string]: OverallPinfallEntry[] } = {};
        for (const divisionLeaderboard of divisionLeaderboards) {
          const division = divisionLeaderboard.description.replace('Division ', '').trim().toLowerCase();
          
          const divisionResponse = await fetch(
            `${apiUrl}/admin/leaderboard/leagues/${leagueId}/overall-pinfall?division=${division}`,
            { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
          );
          
          if (divisionResponse.ok) {
            const data = await divisionResponse.json();
            divisionData[divisionLeaderboard.description] = data;
          }
        }
        setDivisionPinfallData(divisionData);
      }

      // Fetch Most Points data (team points from Leaderboard table)
      const pointsResponse = await fetch(`${apiUrl}/admin/leaderboard/leagues/${leagueId}/most-points`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (pointsResponse.ok) {
        const pointsData = await pointsResponse.json();
        
        setMostPointsData(pointsData);
      }

      // Fetch Overall Pinfall data (individual bowler pinfall from BowlerGameScores)
      const pinfallResponse = await fetch(`${apiUrl}/admin/leaderboard/leagues/${leagueId}/overall-pinfall`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (pinfallResponse.ok) {
        const pinfallData = await pinfallResponse.json();
        
        setOverallPinfallData(pinfallData);
      }

      // Fetch Overall Team Pinfall data (team pinfall from TeamGameScores)
      const teamPinfallResponse = await fetch(`${apiUrl}/admin/leaderboard/leagues/${leagueId}/overall-team-pinfall`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (teamPinfallResponse.ok) {
        const teamPinfallData = await teamPinfallResponse.json();
        
        setOverallTeamPinfallData(teamPinfallData);
      }

    } catch (error) {
      console.error('Error fetching leaderboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [leagueId]);

  useEffect(() => {
    fetchLeaderboardData();
    
    // Inject custom scrollbar styles
    const styleElement = document.createElement('style');
    styleElement.innerHTML = scrollbarStyles;
    document.head.appendChild(styleElement);
    
    return () => {
      document.head.removeChild(styleElement);
    };
  }, [fetchLeaderboardData]);

  const handleAddPayout = async () => {
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      
      const response = await fetch(`${apiUrl}/admin/leaderboard/leagues/${leagueId}/payout-settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          name: newPayout.name,
          description: newPayout.description,
          placement: newPayout.placement,
          type: newPayout.type,
          amount: newPayout.type === 'money' ? newPayout.amount : 0,
          item_desc: newPayout.type === 'item' ? newPayout.item_desc : null
        })
      });

      if (response.ok) {
        setShowAddPayoutModal(false);
        setShowSuccessModal(true);
        // Reset form
        setNewPayout({
          name: 'Most Points',
          description: '',
          placement: 1,
          type: 'money',
          amount: 0,
          item_desc: ''
        });
        // Refresh data
        fetchLeaderboardData();
      } else {
        const errorData = await response.json();
        setErrorMessage(errorData.error || 'Failed to add payout setting');
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error('Error adding payout setting:', error);
      setErrorMessage('An error occurred while adding the payout setting');
      setShowErrorModal(true);
    }
  };

  const handlePrintLeaderboard = () => {
    const [name, description] = activeTab.split('|||');
    const title = name === 'Overall Pinfall' && description && description.startsWith('Division ')
      ? `${name} - ${description}`
      : name;

    const logoUrl = `${window.location.origin}/BowlerProLogo1.png`;
    const generatedDate = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

    // Determine which data and columns to print
    let tableHTML = '';

    const isMostPoints = name === 'Most Points';
    const isTeamPinfall = name === 'Overall Team Pinfall';
    const isDivisionPinfall = name === 'Overall Pinfall' && description && description.startsWith('Division ');
    const isOverallPinfall = name === 'Overall Pinfall';

    if (isMostPoints) {
      tableHTML = `
        <table>
          <thead><tr>
            <th>Rank</th><th>Team</th><th>Points</th>
          </tr></thead>
          <tbody>
            ${mostPointsData.map((e, i) => `
              <tr class="${i % 2 === 0 ? 'even' : ''}">
                <td class="rank">${ `#${i + 1}`}</td>
                <td class="name">${e.team_name}</td>
                <td class="num">${e.points}</td>
              </tr>`).join('')}
          </tbody>
        </table>`;
    } else if (isTeamPinfall) {
      tableHTML = `
        <table>
          <thead><tr>
            <th>Rank</th><th>Team</th><th>Average</th><th>Total Pinfall</th><th>Games</th>
          </tr></thead>
          <tbody>
            ${overallTeamPinfallData.map((e, i) => `
              <tr class="${i % 2 === 0 ? 'even' : ''}">
                <td class="rank">${`#${i + 1}`}</td>
                <td class="name">${e.team_name}</td>
                <td class="num">${e.average}</td>
                <td class="num">${e.total_pinfall}</td>
                <td class="num">${e.games_played}</td>
              </tr>`).join('')}
          </tbody>
        </table>`;
    } else if (isDivisionPinfall) {
      const data = divisionPinfallData[description] || [];
      tableHTML = `
        <table>
          <thead><tr>
            <th>Rank</th><th>Bowler</th><th>Team</th><th>Average</th><th>Total Pinfall</th><th>Games</th>
          </tr></thead>
          <tbody>
            ${data.map((e, i) => `
              <tr class="${i % 2 === 0 ? 'even' : ''}">
                <td class="rank">${`#${i + 1}`}</td>
                <td class="name">${e.bowler_name}</td>
                <td class="team">${e.team_name}</td>
                <td class="num">${e.average}</td>
                <td class="num">${e.total_pinfall}</td>
                <td class="num">${e.games_played}</td>
              </tr>`).join('')}
          </tbody>
        </table>`;
    } else if (isOverallPinfall) {
      tableHTML = `
        <table>
          <thead><tr>
            <th>Rank</th><th>Bowler</th><th>Team</th><th>Average</th><th>Total Pinfall</th><th>Games</th>
          </tr></thead>
          <tbody>
            ${overallPinfallData.map((e, i) => `
              <tr class="${i % 2 === 0 ? 'even' : ''}">
                <td class="rank">${`#${i + 1}`}</td>
                <td class="name">${e.bowler_name}</td>
                <td class="team">${e.team_name}</td>
                <td class="num">${e.average}</td>
                <td class="num">${e.total_pinfall}</td>
                <td class="num">${e.games_played}</td>
              </tr>`).join('')}
          </tbody>
        </table>`;
    }

    const html = `<!DOCTYPE html>
<html>
<head>
  <title>${title} — ${leagueName || 'Leaderboard'}</title>
  <style>
    @page { size: letter portrait; margin: 0.5in; }
    * { box-sizing: border-box; }
    body { font-family: Arial, sans-serif; color: #111; background: white; margin: 0; padding: 0; }
    .header { display: flex; align-items: flex-start; justify-content: space-between; border-bottom: 3px solid #1e3a8a; padding-bottom: 12px; margin-bottom: 6px; }
    .header-left { flex: 1; }
    .league-name { font-size: 13px; color: #555; text-transform: uppercase; letter-spacing: 1px; }
    .header-title { font-size: 26px; font-weight: bold; color: #1e3a8a; margin: 4px 0 2px; }
    .header-date { font-size: 11px; color: #777; }
    .header-logo { text-align: right; }
    .header-logo img { height: 48px; width: auto; }
    .subtitle { text-align: center; font-size: 12px; text-transform: uppercase; letter-spacing: 2px; color: #444; font-weight: 600; margin-bottom: 16px; }
    table { width: 100%; border-collapse: collapse; font-size: 13px; }
    thead tr { background: #1e3a8a; color: white; }
    th { padding: 10px 12px; text-align: left; font-weight: 600; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; }
    td { padding: 9px 12px; border-bottom: 1px solid #e5e7eb; }
    tr.even td { background: #f8faff; }
    .rank { font-weight: bold; text-align: center; width: 60px; font-size: 15px; }
    .name { font-weight: 600; }
    .team { color: #555; }
    .num { text-align: right; font-variant-numeric: tabular-nums; }
    .footer { margin-top: 24px; text-align: center; font-size: 10px; color: #aaa; border-top: 1px solid #e5e7eb; padding-top: 8px; }
    @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
  </style>
</head>
<body>
  <div class="header">
    <div class="header-left">
      ${leagueName ? `<div class="league-name">${leagueName}</div>` : ''}
      <div class="header-title">${title}</div>
      <div class="header-date">Generated ${generatedDate}</div>
    </div>
    <div class="header-logo">
      <img src="${logoUrl}" alt="BowlerPro" onerror="this.style.display='none'" />
    </div>
  </div>
  <div class="subtitle">Official Standings</div>
  ${tableHTML}
  <div class="footer">BowlerPro — Printed ${generatedDate}</div>
</body>
</html>`;

    const win = window.open('', '_blank');
    if (win) {
      win.document.write(html);
      win.document.close();
      win.focus();
      setTimeout(() => win.print(), 200);
    }
  };

  const renderPayoutSettings = () => {
    if (payoutSettings.length === 0) {
      return (
        <div className="bg-yellow-500/20 border border-yellow-400 rounded-lg p-4 mb-6">
          <p className="text-yellow-200 font-semibold">{t('admin.leaderboard.noPayoutSettings')}</p>
          <p className="text-yellow-300 text-sm">
            {t('admin.leaderboard.noPayoutSettingsDesc')}
          </p>
        </div>
      );
    }

    // Group payouts by unique leaderboard (name + description)
    const groupedPayouts: { [key: string]: PayoutSettings } = {};
    
    payoutSettings.forEach(setting => {
      const key = `${setting.name}|||${setting.description}`;
      if (!groupedPayouts[key]) {
        groupedPayouts[key] = {
          name: setting.name,
          description: setting.description,
          payouts: []
        };
      }
      groupedPayouts[key].payouts.push(...setting.payouts);
    });

    return (
      <div className="overflow-x-auto custom-scrollbar">
        <div className="flex space-x-4 pb-4" style={{ minWidth: 'max-content' }}>
          {Object.entries(groupedPayouts).map(([key, setting]) => (
            <div key={key} className="bg-white/10 backdrop-blur-lg rounded-lg p-3 border border-white/20 flex-shrink-0 w-64">
              <h3 className="text-lg font-bold text-white mb-2 truncate">{setting.name}</h3>
              <p className="text-blue-200 text-sm mb-3 line-clamp-2">{setting.description}</p>
              <div className="space-y-1 max-h-32 overflow-y-auto custom-scrollbar">
                {setting.payouts.map((payout, payoutIndex) => (
                  <div key={payoutIndex} className="flex justify-between text-sm">
                    <span className="text-white">#{payout.placement}</span>
                    <span className="text-green-300 truncate ml-2">
                      {payout.type === 'money' ? `$${payout.amount}` : payout.item_desc}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderMostPointsLeaderboard = () => {
    if (mostPointsData.length === 0) {
      return <div className="text-center text-blue-200 py-8">{t('admin.leaderboard.mostPoints.noData')}</div>;
    }

    return (
      <div className="bg-white/10 backdrop-blur-lg rounded-lg overflow-hidden">
        <div className="bg-blue-600/20 px-4 py-2 border-b border-white/10">
          <p className="text-blue-200 text-sm">
            {t('admin.leaderboard.mostPoints.dataSource', { count: mostPointsData.length })}
          </p>
        </div>
        <div className="overflow-x-auto custom-scrollbar">
          <table className="w-full">
            <thead className="bg-white/20">
              <tr>
                <th className="px-6 py-3 text-left text-white font-semibold">{t('admin.leaderboard.mostPoints.headers.rank')}</th>
                <th className="px-6 py-3 text-left text-white font-semibold">{t('admin.leaderboard.mostPoints.headers.team')}</th>
                <th className="px-6 py-3 text-right text-white font-semibold">{t('admin.leaderboard.mostPoints.headers.points')}</th>
                <th className="px-6 py-3 text-center text-white font-semibold">{t('admin.leaderboard.mostPoints.headers.action')}</th>
              </tr>
            </thead>
            <tbody>
              {mostPointsData.map((entry, index) => (
                <tr key={entry.team_id} className="border-b border-white/10 hover:bg-white/5">
                  <td className="px-6 py-4 text-white font-semibold">#{index + 1}</td>
                  <td className="px-6 py-4 text-white">{entry.team_name}</td>
                  <td className="px-6 py-4 text-right text-green-300 font-semibold">{entry.points}</td>
                  <td className="px-6 py-4 text-center">
                    <div className="flex space-x-2 justify-center">
                      <button
                        onClick={() => setSelectedTeam({ id: entry.team_id, name: entry.team_name })}
                        className="bg-blue-600/50 hover:bg-blue-700/50 text-white px-3 py-1 rounded text-sm transition duration-200"
                      >
                        {t('admin.leaderboard.mostPoints.viewDetails')}
                      </button>
                      <button
                        onClick={() => onNavigateToFinancial?.()}
                        className="bg-green-600/50 hover:bg-green-700/50 text-white px-3 py-1 rounded text-sm transition duration-200"
                      >
                        {t('admin.leaderboard.mostPoints.goToPayout')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderOverallPinfallLeaderboard = () => {
    if (overallPinfallData.length === 0) {
      return <div className="text-center text-blue-200 py-8">{t('admin.leaderboard.overallPinfall.noData')}</div>;
    }

    return (
      <div className="bg-white/10 backdrop-blur-lg rounded-lg overflow-hidden">
        <div className="bg-purple-600/20 px-4 py-2 border-b border-white/10">
          <p className="text-purple-200 text-sm">
            {t('admin.leaderboard.overallPinfall.dataSource', { count: overallPinfallData.length })}
          </p>
        </div>
        <div className="overflow-x-auto custom-scrollbar">
          <table className="w-full">
            <thead className="bg-white/20">
              <tr>
                <th className="px-6 py-3 text-left text-white font-semibold">{t('admin.leaderboard.overallPinfall.headers.rank')}</th>
                <th className="px-6 py-3 text-left text-white font-semibold">{t('admin.leaderboard.overallPinfall.headers.bowler')}</th>
                <th className="px-6 py-3 text-left text-white font-semibold">{t('admin.leaderboard.overallPinfall.headers.team')}</th>
                <th className="px-6 py-3 text-right text-white font-semibold">{t('admin.leaderboard.overallPinfall.headers.average')}</th>
                <th className="px-6 py-3 text-right text-white font-semibold">{t('admin.leaderboard.overallPinfall.headers.totalPinfall')}</th>
                <th className="px-6 py-3 text-right text-white font-semibold">{t('admin.leaderboard.overallPinfall.headers.games')}</th>
                <th className="px-6 py-3 text-center text-white font-semibold">{t('admin.leaderboard.overallPinfall.headers.action')}</th>
              </tr>
            </thead>
            <tbody>
              {overallPinfallData.map((entry, index) => (
                <tr key={entry.bowler_id} className="border-b border-white/10 hover:bg-white/5">
                  <td className="px-6 py-4 text-white font-semibold">#{index + 1}</td>
                  <td className="px-6 py-4 text-white">{entry.bowler_name}</td>
                  <td className="px-6 py-4 text-blue-200">{entry.team_name}</td>
                  <td className="px-6 py-4 text-right text-green-300 font-semibold">{entry.average}</td>
                  <td className="px-6 py-4 text-right text-white">{entry.total_pinfall}</td>
                  <td className="px-6 py-4 text-right text-gray-300">{entry.games_played}</td>
                  <td className="px-6 py-4 text-center">
                    <div className="flex space-x-2 justify-center">
                      <button
                        onClick={() => setSelectedBowler({ 
                          id: entry.bowler_id, 
                          name: entry.bowler_name, 
                          teamName: entry.team_name 
                        })}
                        className="bg-blue-600/50 hover:bg-blue-700/50 text-white px-3 py-1 rounded text-sm transition duration-200"
                      >
                        {t('admin.leaderboard.mostPoints.viewDetails')}
                      </button>
                      <button
                        onClick={() => onNavigateToFinancial?.()}
                        className="bg-green-600/50 hover:bg-green-700/50 text-white px-3 py-1 rounded text-sm transition duration-200"
                      >
                        {t('admin.leaderboard.mostPoints.goToPayout')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderOverallTeamPinfallLeaderboard = () => {
    if (overallTeamPinfallData.length === 0) {
      return <div className="text-center text-blue-200 py-8">{t('admin.leaderboard.overallTeamPinfall.noData')}</div>;
    }

    return (
      <div className="bg-white/10 backdrop-blur-lg rounded-lg overflow-hidden">
        <div className="bg-orange-600/20 px-4 py-2 border-b border-white/10">
          <p className="text-orange-200 text-sm">
            {t('admin.leaderboard.overallTeamPinfall.dataSource', { count: overallTeamPinfallData.length })}
          </p>
        </div>
        <div className="overflow-x-auto custom-scrollbar">
          <table className="w-full">
            <thead className="bg-white/20">
              <tr>
                <th className="px-6 py-3 text-left text-white font-semibold">{t('admin.leaderboard.overallTeamPinfall.headers.rank')}</th>
                <th className="px-6 py-3 text-left text-white font-semibold">{t('admin.leaderboard.overallTeamPinfall.headers.team')}</th>
                <th className="px-6 py-3 text-right text-white font-semibold">{t('admin.leaderboard.overallTeamPinfall.headers.average')}</th>
                <th className="px-6 py-3 text-right text-white font-semibold">{t('admin.leaderboard.overallTeamPinfall.headers.totalPinfall')}</th>
                <th className="px-6 py-3 text-right text-white font-semibold">{t('admin.leaderboard.overallTeamPinfall.headers.games')}</th>
                <th className="px-6 py-3 text-center text-white font-semibold">{t('admin.leaderboard.overallTeamPinfall.headers.action')}</th>
              </tr>
            </thead>
            <tbody>
              {overallTeamPinfallData.map((entry, index) => (
                <tr key={entry.team_id} className="border-b border-white/10 hover:bg-white/5">
                  <td className="px-6 py-4 text-white font-semibold">#{index + 1}</td>
                  <td className="px-6 py-4 text-white">{entry.team_name}</td>
                  <td className="px-6 py-4 text-right text-green-300 font-semibold">{entry.average}</td>
                  <td className="px-6 py-4 text-right text-white">{entry.total_pinfall}</td>
                  <td className="px-6 py-4 text-right text-gray-300">{entry.games_played}</td>
                  <td className="px-6 py-4 text-center">
                    <div className="flex space-x-2 justify-center">
                      <button
                        onClick={() => setSelectedTeam({ id: entry.team_id, name: entry.team_name })}
                        className="bg-blue-600/50 hover:bg-blue-700/50 text-white px-3 py-1 rounded text-sm transition duration-200"
                      >
                        {t('admin.leaderboard.mostPoints.viewDetails')}
                      </button>
                      <button
                        onClick={() => onNavigateToFinancial?.()}
                        className="bg-green-600/50 hover:bg-green-700/50 text-white px-3 py-1 rounded text-sm transition duration-200"
                      >
                        {t('admin.leaderboard.mostPoints.goToPayout')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderDivisionPinfallLeaderboard = (divisionDescription: string) => {
    const data = divisionPinfallData[divisionDescription];
    
    if (!data || data.length === 0) {
      return <div className="text-center text-blue-200 py-8">{t('admin.leaderboard.divisionPinfall.noData')}</div>;
    }

    return (
      <div className="bg-white/10 backdrop-blur-lg rounded-lg overflow-hidden">
        <div className="bg-purple-600/20 px-4 py-2 border-b border-white/10">
          <p className="text-purple-200 text-sm">
            {t('admin.leaderboard.divisionPinfall.dataSource', { division: divisionDescription, count: data.length })}
          </p>
        </div>
        <div className="overflow-x-auto custom-scrollbar">
          <table className="w-full">
            <thead className="bg-white/20">
              <tr>
                <th className="px-6 py-3 text-left text-white font-semibold">{t('admin.leaderboard.overallPinfall.headers.rank')}</th>
                <th className="px-6 py-3 text-left text-white font-semibold">{t('admin.leaderboard.overallPinfall.headers.bowler')}</th>
                <th className="px-6 py-3 text-left text-white font-semibold">{t('admin.leaderboard.overallPinfall.headers.team')}</th>
                <th className="px-6 py-3 text-right text-white font-semibold">{t('admin.leaderboard.overallPinfall.headers.average')}</th>
                <th className="px-6 py-3 text-right text-white font-semibold">{t('admin.leaderboard.overallPinfall.headers.totalPinfall')}</th>
                <th className="px-6 py-3 text-right text-white font-semibold">{t('admin.leaderboard.overallPinfall.headers.games')}</th>
                <th className="px-6 py-3 text-center text-white font-semibold">{t('admin.leaderboard.overallPinfall.headers.action')}</th>
              </tr>
            </thead>
            <tbody>
              {data.map((entry, index) => (
                <tr key={entry.bowler_id} className="border-b border-white/10 hover:bg-white/5">
                  <td className="px-6 py-4 text-white font-semibold">#{index + 1}</td>
                  <td className="px-6 py-4 text-white">{entry.bowler_name}</td>
                  <td className="px-6 py-4 text-blue-200">{entry.team_name}</td>
                  <td className="px-6 py-4 text-right text-green-300 font-semibold">{entry.average}</td>
                  <td className="px-6 py-4 text-right text-white">{entry.total_pinfall}</td>
                  <td className="px-6 py-4 text-right text-gray-300">{entry.games_played}</td>
                  <td className="px-6 py-4 text-center">
                    <div className="flex space-x-2 justify-center">
                      <button
                        onClick={() => setSelectedBowler({ 
                          id: entry.bowler_id, 
                          name: entry.bowler_name, 
                          teamName: entry.team_name 
                        })}
                        className="bg-blue-600/50 hover:bg-blue-700/50 text-white px-3 py-1 rounded text-sm transition duration-200"
                      >
                        {t('admin.leaderboard.mostPoints.viewDetails')}
                      </button>
                      <button
                        onClick={() => onNavigateToFinancial?.()}
                        className="bg-green-600/50 hover:bg-green-700/50 text-white px-3 py-1 rounded text-sm transition duration-200"
                      >
                        {t('admin.leaderboard.mostPoints.goToPayout')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderActiveLeaderboard = () => {
    // Parse the active tab key to get name and description
    const [name, description] = activeTab.split('|||');
    
    // Check if this is a division-specific Overall Pinfall leaderboard
    if (name === 'Overall Pinfall' && description && description.startsWith('Division ')) {
      return renderDivisionPinfallLeaderboard(description);
    }
    
    // Handle exact matches for standard leaderboards
    switch (name) {
      case 'Most Points':
        return renderMostPointsLeaderboard();
      case 'Overall Pinfall':
        // Only show overall (non-division) if description is "Overall"
        if (description === 'Overall') {
          return renderOverallPinfallLeaderboard();
        }
        return renderOverallPinfallLeaderboard();
      case 'Overall Team Pinfall':
        return renderOverallTeamPinfallLeaderboard();
    }
    
    // Handle case-insensitive and partial matches for flexibility
    const nameLower = name?.toLowerCase() || '';
    if (nameLower.includes('point')) {
      return renderMostPointsLeaderboard();
    } else if (nameLower.includes('individual') || (nameLower.includes('pinfall') && nameLower.includes('bowler'))) {
      return renderOverallPinfallLeaderboard();
    } else if (nameLower.includes('team') && nameLower.includes('pinfall')) {
      return renderOverallTeamPinfallLeaderboard();
    }
    
    // Default to points leaderboard if no clear match
    return renderMostPointsLeaderboard();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-800 flex items-center justify-center">
        <div className="text-white text-xl">{t('admin.leaderboard.loading')}</div>
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-800">
      {/* Header */}
      <div className="bg-white/10 backdrop-blur-lg border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              <button
                onClick={onBack}
                className="text-white hover:text-blue-300 transition duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h1 className="text-3xl font-bold text-white">{t('admin.leaderboard.title')}</h1>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Payout Settings Section */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <h2 className="text-2xl font-bold text-white mr-4">{t('admin.leaderboard.payoutSettings')}</h2>
             {(staffRoles?.includes(9)) && (
            <button
              onClick={() => setShowAddPayoutModal(true)}
              className="bg-yellow-600/40 hover:bg-yellow-700/40 text-white px-2 py-1 rounded-lg transition duration-200 flex items-center"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <span>{t('admin.leaderboard.addButton')}</span>
            </button>
            )}
          </div>
          {renderPayoutSettings()}
        </div>

        {/* Leaderboard Tabs */}
        <div className="mb-6">
          <div className="flex space-x-1 bg-white/10 p-1 rounded-lg overflow-x-auto custom-scrollbar">
            {(() => {
              // Create unique tabs based on name + description combination
              const uniqueTabs = new Map<string, { name: string; description: string }>();
              payoutSettings.forEach((setting) => {
                const key = `${setting.name}|||${setting.description}`;
                if (!uniqueTabs.has(key)) {
                  uniqueTabs.set(key, { name: setting.name, description: setting.description });
                }
              });
              
              return Array.from(uniqueTabs.entries()).map(([key, setting]) => (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  className={`px-4 py-2 rounded-md transition duration-200 whitespace-nowrap flex-shrink-0 ${
                    activeTab === key
                      ? 'bg-blue-600 text-white'
                      : 'text-blue-200 hover:bg-white/10'
                  }`}
                >
                  {setting.name === 'Overall Pinfall' && setting.description && setting.description.startsWith('Division ')
                    ? `${setting.name} - ${setting.description}`
                    : setting.name}
                </button>
              ));
            })()}
          </div>
        </div>

        {/* Active Leaderboard */}
        <div className="mb-8">
          {(() => {
            const [name, description] = activeTab.split('|||');
            const title = name === 'Overall Pinfall' && description && description.startsWith('Division ')
              ? `${name} - ${description}`
              : name;
            return (
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-white">🏆 {title}</h2>
                <button
                  onClick={handlePrintLeaderboard}
                  className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-3 py-2 rounded-lg text-sm font-medium transition duration-200 border border-white/20"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                  </svg>
                  Print Standings
                </button>
              </div>
            );
          })()}
          {renderActiveLeaderboard()}
        </div>
      </div>

      {/* Detail Modals */}
      {selectedTeam && (
        <LeaderboardTeamDetail
          teamId={selectedTeam.id}
          teamName={selectedTeam.name}
          leagueId={leagueId}
          onClose={() => setSelectedTeam(null)}
        />
      )}

      {selectedBowler && (
        <LeaderboardBowlerDetail
          bowlerId={selectedBowler.id}
          bowlerName={selectedBowler.name}
          teamName={selectedBowler.teamName}
          leagueId={leagueId}
          onClose={() => setSelectedBowler(null)}
        />
      )}

      {/* Add Payout Modal */}
      {showAddPayoutModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 max-w-2xl w-full max-h-[90vh] overflow-y-auto custom-scrollbar">
            {/* Header */}
            <div className="flex justify-between items-center p-6 border-b border-white/20">
              <h2 className="text-2xl font-bold text-white">{t('admin.leaderboard.addPayoutModal.title')}</h2>
              <button
                onClick={() => setShowAddPayoutModal(false)}
                className="text-white hover:text-red-300 transition duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Leaderboard Name */}
              <div>
                <label className="block text-sm font-medium text-purple-200 mb-2">
                  {t('admin.leaderboard.addPayoutModal.leaderboardName')}
                </label>
                <select 
                  value={newPayout.name} 
                  onChange={(e) => setNewPayout({ ...newPayout, name: e.target.value })}
                  className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none cursor-pointer"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23ffffff' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                    backgroundPosition: 'right 0.5rem center',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: '1.5em 1.5em',
                    paddingRight: '2.5rem'
                  }}
                >
                  <option value="Most Points" className="bg-gray-800">{t('admin.leaderboard.addPayoutModal.leaderboardOptions.mostPoints')}</option>
                  <option value="Overall Pinfall" className="bg-gray-800">{t('admin.leaderboard.addPayoutModal.leaderboardOptions.overallPinfall')}</option>
                  <option value="Overall Team Pinfall" className="bg-gray-800">{t('admin.leaderboard.addPayoutModal.leaderboardOptions.overallTeamPinfall')}</option>
                </select>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-purple-200 mb-2">
                  {t('admin.leaderboard.addPayoutModal.description')}
                </label>
                <input
                  type="text"
                  value={newPayout.description}
                  onChange={(e) => setNewPayout({ ...newPayout, description: e.target.value })}
                  className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={t('admin.leaderboard.addPayoutModal.descriptionPlaceholder')}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Placement */}
                <div>
                  <label className="block text-sm font-medium text-purple-200 mb-2">
                    {t('admin.leaderboard.addPayoutModal.placement')}
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={newPayout.placement}
                    onChange={(e) => setNewPayout({ ...newPayout, placement: parseInt(e.target.value) || 1 })}
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Type */}
                <div>
                  <label className="block text-sm font-medium text-purple-200 mb-2">
                    {t('admin.leaderboard.addPayoutModal.payoutType')}
                  </label>
                  <select
                    value={newPayout.type}
                    onChange={(e) => setNewPayout({ ...newPayout, type: e.target.value as 'money' | 'item' })}
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="money" className="bg-gray-800">{t('admin.leaderboard.addPayoutModal.payoutTypes.money')}</option>
                    <option value="item" className="bg-gray-800">{t('admin.leaderboard.addPayoutModal.payoutTypes.item')}</option>
                  </select>
                </div>
              </div>

              {/* Amount or Item Description */}
              {newPayout.type === 'money' ? (
                <div>
                  <label className="block text-sm font-medium text-purple-200 mb-2">
                    {t('admin.leaderboard.addPayoutModal.amount')}
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={newPayout.amount}
                    onChange={(e) => setNewPayout({ ...newPayout, amount: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-purple-200 mb-2">
                    {t('admin.leaderboard.addPayoutModal.itemDescription')}
                  </label>
                  <input
                    type="text"
                    value={newPayout.item_desc}
                    onChange={(e) => setNewPayout({ ...newPayout, item_desc: e.target.value })}
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={t('admin.leaderboard.addPayoutModal.itemPlaceholder')}
                  />
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-white/20 flex justify-end space-x-3">
              <button
                onClick={() => setShowAddPayoutModal(false)}
                className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition duration-200"
              >
                {t('admin.leaderboard.addPayoutModal.cancel')}
              </button>
              <button
                onClick={handleAddPayout}
                disabled={!newPayout.description || (newPayout.type === 'money' && newPayout.amount <= 0) || (newPayout.type === 'item' && !newPayout.item_desc)}
                className="bg-green-600 hover:bg-green-700 disabled:bg-gray-500 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg transition duration-200"
              >
                {t('admin.leaderboard.addPayoutModal.add')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 max-w-md w-full">
            <div className="p-6 text-center">
              <div className="text-6xl mb-4">✅</div>
              <h2 className="text-2xl font-bold text-white mb-4">{t('admin.leaderboard.successModal.title')}</h2>
              <p className="text-green-200 mb-6">
                {t('admin.leaderboard.successModal.message')}
              </p>
              <button
                onClick={() => setShowSuccessModal(false)}
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg transition duration-200"
              >
                {t('admin.leaderboard.successModal.button')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 max-w-md w-full">
            <div className="p-6 text-center">
              <div className="text-6xl mb-4">❌</div>
              <h2 className="text-2xl font-bold text-white mb-4">{t('admin.leaderboard.errorModal.title')}</h2>
              <p className="text-red-200 mb-6">
                {errorMessage}
              </p>
              <button
                onClick={() => setShowErrorModal(false)}
                className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition duration-200"
              >
                {t('admin.leaderboard.errorModal.button')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Leaderboard;